import { clsx, type ClassValue } from "clsx";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// A small local set of primitives rather than shadcn's generated component tree.
// This console has one audience and a handful of screens; pulling in the generated
// set would add far more surface than it saves here. If it grows, the primitives
// below are the seam to replace.

export function Button({
  variant = "primary",
  className,
  ...props
}: React.ComponentPropsWithRef<"button"> & {
  variant?: "primary" | "secondary" | "danger";
}) {
  const variants = {
    primary: "bg-slate-900 text-white hover:bg-slate-700",
    secondary: "bg-white text-slate-900 ring-1 ring-slate-300 hover:bg-slate-50",
    // Darker on hover, unlike primary above, because this one is red: white on
    // red-500 is 3.8:1, under the 4.5:1 floor, so lightening on hover put the label
    // of the console's destructive control below AA at the moment somebody is
    // reading it to decide. red-700 keeps the state change and stays legible.
    danger: "bg-red-600 text-white hover:bg-red-700"
  } as const;
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium",
        "disabled:cursor-not-allowed disabled:opacity-50",
        variants[variant],
        className
      )}
      {...props}
    />
  );
}

export function Field({
  label,
  hint,
  required,
  children
}: {
  label: string;
  hint?: string;
  /**
   * Marks the field as one the request will be rejected without.
   *
   * Deliberately not wired to the input's own `required` attribute: the submit
   * button is disabled until the form is complete, so native validation would never
   * fire, and a field that is one of an either/or pair (work or personal email) is
   * required as a group rather than individually. The marker states the fact; the
   * guard enforces it.
   */
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-slate-700">
        {label}
        {required ? (
          <span className="ml-0.5 text-red-600" title="Required">
            *<span className="sr-only"> (required)</span>
          </span>
        ) : null}
      </span>
      {children}
      {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

const controlClass =
  "rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-slate-500 focus:outline-none";

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(controlClass, props.className)} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn(controlClass, "bg-white", props.className)} />;
}

// There is no Textarea. INFRA-593 added one for the organization alias box — its only
// caller ever — and INFRA-671 removed that box, so it went with it rather than staying
// on as an untested primitive nothing renders. Nothing in this console asks for
// multi-line free text; if something does, add it back with the field that needs it.

export type RadioOption<T extends string> = {
  value: T;
  label: string;
  /**
   * What this option means, beside its label.
   *
   * For a set whose labels do not separate themselves — where one option is a kind of
   * another, so a reader cannot tell from the names alone which to pick. Giving any
   * option a hint stacks the whole group, since hints do not fit on one line.
   */
  hint?: string;
};

/**
 * A small set of mutually exclusive choices, all visible at once.
 *
 * Preferred over a Select wherever the point is seeing the options *without*
 * interacting: a closed dropdown shows one of three levels and hides the rest, which
 * is the one thing an access screen exists to make obvious.
 *
 * Deliberately not built on Field, which wraps its children in a `<label>`. A label
 * containing several radios associates with the first of them, so clicking the
 * group's name would silently select the first option. `<fieldset>` with a
 * `<legend>` is the element that actually names a group of controls, and it disables
 * every descendant input from the one attribute.
 *
 * `name` must be unique per rendered group. Two groups sharing a name are one radio
 * group as far as the browser is concerned, so choosing in either clears the other —
 * and on a screen that renders a group per table row, that means editing one
 * person's access silently blanks another's.
 */
export function RadioGroup<T extends string>({
  name,
  legend,
  hint,
  value,
  options,
  disabled,
  onChange
}: {
  name: string;
  legend: string;
  hint?: string;
  value: T;
  options: readonly RadioOption<T>[];
  disabled?: boolean;
  onChange: (value: T) => void;
}) {
  // One group is stacked or inline as a whole, never per option: a hint on the second
  // of five choices would otherwise pull that one row out of line with its siblings.
  const hinted = options.some((option) => option.hint);

  return (
    <fieldset disabled={disabled} className="min-w-0 border-0 p-0">
      <legend className="mb-1 text-sm font-medium text-slate-700">
        {legend}
        {hint ? <span className="ml-1.5 font-normal text-slate-500">{hint}</span> : null}
      </legend>
      <div
        className={cn(
          "text-sm",
          hinted ? "flex flex-col gap-1.5" : "flex flex-wrap items-center gap-3"
        )}
      >
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex gap-1.5",
              hinted ? "items-start" : "items-center whitespace-nowrap"
            )}
          >
            <input
              type="radio"
              className={hinted ? "mt-1" : undefined}
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span>
              {option.label}
              {option.hint ? (
                <span className="ml-1.5 text-xs text-slate-500">{option.hint}</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export type ComboboxOption = {
  /** The value committed on selection. */
  value: string;
  /** What the option and the closed control read as. */
  label: string;
  /** Secondary text on the option row, and matched by the search. */
  hint?: string;
};

/**
 * A single-select picker for a list too long for a `<Select>`: type to filter, arrows
 * and Enter to choose.
 *
 * Hand-rolled for the reason the rest of this file is, and the reason `ConfirmDialog`
 * was: one audience, a handful of screens, and nothing in the dependencies that fits.
 *
 * **The committed value can only ever be an option's value.** Typing filters and does
 * nothing else — `onChange` fires from a click or from Enter on a highlighted row, and
 * a query that matches nothing has nothing to highlight. That is the point of using
 * this rather than an `<Input>` for work country: the field holds an ISO 3166-1 code
 * that the backend validates against its own list, so a text box would let a
 * plausible-looking wrong value through and surface it only as a rejected save.
 */
export function Combobox({
  label,
  value,
  options,
  onChange,
  disabled,
  placeholder = "Search…",
  emptyLabel,
  noMatchesLabel = "No matches"
}: {
  /**
   * The field's name, repeated from the enclosing `Field`.
   *
   * Stated here rather than left to the wrapping `<label>` because a wrapper label
   * contributes *all* of its text to the control's accessible name — including the
   * option list while it is open, which would have a screen reader announce every
   * visible country as part of the field's name. It also keeps
   * `getByLabelText("Work country")` finding this input whether the list is open or
   * shut.
   */
  label: string;
  value: string;
  options: readonly ComboboxOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Label for the option that clears the field, offered only while nothing has been
   * typed. Omitting it makes the field unclearable through this control.
   *
   * Hidden once there is a query so that Enter after a query with no matches cannot
   * land on it: that keystroke would read as "choose what I typed" and would in fact
   * erase whatever was already chosen.
   */
  emptyLabel?: string;
  noMatchesLabel?: string;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  // Falls back to the raw value, not to "": a stored code the options do not cover —
  // because the lookups query has not landed yet, or failed — would otherwise render as
  // an empty box, which reads as "nothing recorded" for a field that holds an answer.
  // The identity list does the same thing with its column.
  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  const matches = useMemo(() => rankOptions(options, query), [options, query]);
  const choices = useMemo(
    () =>
      query.trim() === "" && emptyLabel !== undefined
        ? [{ value: "", label: emptyLabel }, ...matches]
        : matches,
    [matches, query, emptyLabel]
  );

  // Clamped on render rather than corrected in the handler: the list shrinks as the
  // query narrows, so the stored index can be past the end by the time it is read,
  // and Enter would then commit nothing with the highlight still visible.
  const activeIndex = Math.min(active, Math.max(choices.length - 1, 0));

  const optionId = (index: number) => `${listId}-option-${index}`;

  const dismiss = () => {
    setOpen(false);
    setQuery("");
  };

  const commit = (choice: ComboboxOption) => {
    onChange(choice.value);
    dismiss();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(0);
        return;
      }
      const next = activeIndex + (e.key === "ArrowDown" ? 1 : -1);
      setActive(Math.max(0, Math.min(next, choices.length - 1)));
      return;
    }
    if (e.key === "Enter") {
      // Only while the list is open, so a closed picker leaves Enter to the form.
      if (!open) {
        return;
      }
      e.preventDefault();
      const choice = choices[activeIndex];
      if (choice) {
        commit(choice);
      }
      return;
    }
    if (e.key === "Escape" && open) {
      e.preventDefault();
      dismiss();
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={open && choices[activeIndex] ? optionId(activeIndex) : undefined}
        // Off, or the browser's own suggestion list covers this one.
        autoComplete="off"
        disabled={disabled}
        // A focus ring on top of the shared focus border: this control opens a list
        // on focus, so where the keyboard is has to be unmistakable.
        className={cn(controlClass, "w-full bg-white focus:ring-1 focus:ring-slate-500")}
        // The query while open, the choice while closed: opening with the selected
        // label in the box would filter the list down to that one row, so changing a
        // choice would start by clearing text.
        value={open ? query : selectedLabel}
        placeholder={open && selectedLabel ? selectedLabel : placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Focus alone is not enough to reopen: choosing an option keeps focus in the
        // input (the list suppresses its own mousedown), so clicking the field again
        // fires no focus event and the list would stay shut.
        //
        // mousedown rather than click, because `Field` wraps all of this in a <label>:
        // a click anywhere inside a label is forwarded to its control, so an onClick
        // here would also fire for the click that chose an option — reopening the list
        // immediately after it closed.
        onMouseDown={() => setOpen(true)}
        // Reverts the query, never the value. Walking away from a half-typed search
        // leaves the field as it was.
        onBlur={dismiss}
        onKeyDown={onKeyDown}
      />
      {/* An SVG rather than a text chevron: a wrapping <label> contributes all of its
          text to the control's accessible name, so a "▾" character would end up part
          of the field's label. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 12 12"
        className="pointer-events-none absolute right-2 top-2.5 h-3 w-3 fill-none stroke-slate-400 stroke-2"
      >
        <path d="M2 4.5 6 8.5 10 4.5" />
      </svg>
      {open ? (
        // preventDefault on mousedown keeps focus in the input, so the click that
        // follows still lands on an option instead of being lost to the blur.
        <ul
          id={listId}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {choices.length === 0 ? (
            <li className="px-2 py-1.5 text-sm text-slate-500">{noMatchesLabel}</li>
          ) : (
            choices.map((choice, index) => (
              <li
                key={choice.value || "__none__"}
                id={optionId(index)}
                role="option"
                aria-selected={choice.value === value}
                onClick={() => commit(choice)}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  "flex cursor-pointer items-baseline gap-1.5 px-2 py-1.5 text-sm",
                  index === activeIndex ? "bg-slate-100" : "",
                  choice.value === value ? "font-medium" : ""
                )}
              >
                <span>{choice.label}</span>
                {choice.hint ? <span className="text-xs text-slate-500">{choice.hint}</span> : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Filters and orders the options for a query, matching label and hint.
 *
 * Ranked rather than merely filtered, because a two-letter code is a substring of
 * plenty of names: for "us", a plain `includes` puts Australia and Belarus above the
 * United States. Exact hint match first, then prefixes, then anything containing it —
 * and the input order is preserved within each rank, so an alphabetical list stays
 * alphabetical.
 */
function rankOptions(options: readonly ComboboxOption[], query: string): readonly ComboboxOption[] {
  const q = query.trim().toLowerCase();
  if (q === "") {
    return options;
  }

  const ranked: { option: ComboboxOption; rank: number }[] = [];
  for (const option of options) {
    const label = option.label.toLowerCase();
    const hint = option.hint?.toLowerCase() ?? "";
    let rank: number | null = null;
    if (hint === q) {
      rank = 0;
    } else if (label.startsWith(q)) {
      rank = 1;
    } else if (hint.startsWith(q)) {
      rank = 2;
    } else if (label.includes(q)) {
      rank = 3;
    }
    if (rank !== null) {
      ranked.push({ option, rank });
    }
  }
  return ranked.sort((a, b) => a.rank - b.rank).map((r) => r.option);
}

export function Card({
  title,
  actions,
  children
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      {title || actions ? (
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          {/* Omitted rather than rendered empty: a card carrying only actions — one
              inside a window that already names itself — would otherwise put an
              empty heading in the outline for a screen reader to announce. */}
          {title ? <h2 className="text-sm font-semibold text-slate-800">{title}</h2> : <span />}
          {actions}
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Badge({
  tone = "slate",
  children
}: {
  tone?: "slate" | "green" | "amber" | "red";
  children: ReactNode;
}) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-green-100 text-green-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800"
  } as const;
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-xs font-medium", tones[tone])}>{children}</span>
  );
}

/** statusTone keeps the term colouring in one place. */
export function statusTone(status: string): "slate" | "green" | "amber" | "red" {
  switch (status) {
    case "ACTIVE":
      return "green";
    case "UPCOMING":
      return "amber";
    // A term that has ended is history, not a problem, so it reads as neutral. Red
    // is reserved for the one thing that does need attention: a term past its end
    // date that nobody closed out, which the detail screen flags separately.
    default:
      return "slate";
  }
}

export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    // Wide tables scroll inside their own container rather than making the page
    // scroll sideways.
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            {/* scope="col" so each header is announced as the heading of its column
                rather than as a stray cell. Every table in this console is a record
                being read across, which is the case the attribute exists for. */}
            {head.map((h) => (
              <th key={h} scope="col" className="px-2 py-2 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-slate-500">{children}</p>;
}

export function humanPopulation(p: string): string {
  return p === "ON_DEMAND_CONTRACTOR" ? "On-demand contractor" : "Partner";
}

/**
 * humanStatus names a derived term position.
 *
 * Spelled out rather than title-cased, because the enum names do not survive it:
 * NOT_STARTED would render as "Not_started", and "Upcoming" on its own does not say
 * that a start date is what is being waited on.
 */
export function humanStatus(s: string): string {
  switch (s) {
    case "NOT_STARTED":
      return "No start date";
    case "UPCOMING":
      return "Starts later";
    case "ACTIVE":
      return "Active";
    case "ENDED":
      return "Ended";
    default:
      return s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, " ");
  }
}

export function humanRole(r: string): string {
  return r.toLowerCase().replace(/_/g, "-");
}

/**
 * Focus stops inside a container, in tab order.
 *
 * No visibility filter, deliberately. jsdom gives every element zero layout, so a
 * check written on `offsetParent` or `getClientRects` would match nothing under test
 * and let the containment tests pass against a trap cycling an empty list. The
 * dialogs this serves author their own content and put nothing hidden in it, so
 * there is nothing for such a filter to earn.
 */
const FOCUS_STOPS = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

/**
 * A confirmation step for an action that is awkward to undo.
 *
 * Hand-rolled rather than pulled from a library, for the same reason the rest of
 * this file is: one audience, a handful of screens. `sonner` is here for toasts and
 * has nothing that fits, and there was no dialog primitive at all before this.
 *
 * Cancel takes focus on open, not confirm. Someone who hit the button by accident
 * and then hits Enter should land on the safe outcome — defaulting to confirm would
 * reproduce the misclick this exists to catch.
 *
 * **Modal in behaviour, not only in name.** `aria-modal="true"` tells a screen
 * reader that the rest of the document is unavailable, so Tab and Shift+Tab cycle
 * within the dialog rather than walking out into a page the reader has just been
 * told is out of play. Containment is the keyboard half of that same promise.
 * Nothing is marked `inert`: this renders inside the screen it covers rather than
 * through a portal, so there are no siblings to mark, and `aria-modal` is what holds
 * a screen reader's own cursor. Worth having rather than dropping the claim because
 * this wraps three destructive or lifecycle decisions — delete an organization, end
 * an engagement, revive one.
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  tone = "danger",
  busy,
  confirmDisabled,
  onConfirm,
  onCancel,
  children
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  tone?: "danger" | "primary";
  busy?: boolean;
  /**
   * Blocks confirmation while the dialog's own inputs are incomplete. Separate from
   * `busy` so the button keeps its own label rather than reading "Working…" when
   * nothing is in flight.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      if (e.key !== "Tab") {
        return;
      }
      const dialog = box.current;
      if (!dialog) {
        return;
      }
      const stops = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUS_STOPS));
      // Nothing inside can hold focus — which `busy` causes, disabling both buttons
      // of a confirmation whose body is only text — so the dialog holds it itself.
      // Letting Tab through would put the keyboard in the page this dialog has told
      // a screen reader is unavailable, which is the defect being fixed.
      //
      // This is also what makes Escape's freedom from `busy`, just above, load
      // bearing rather than an inconsistency with the buttons: in this state it is
      // the only way out for a keyboard, so guarding it would strand the operator.
      if (stops.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const at = stops.indexOf(document.activeElement as HTMLElement);
      // `at === -1` is not defensive padding, and it covers two ordinary states
      // rather than one. Focus can be outside the dialog with nobody having tabbed
      // anywhere, because disabling the button that holds it drops it to <body> —
      // which is what confirming does to the confirm button. And it can be inside on
      // something that is not a stop: the dialog itself, from the branch above. Both
      // have to re-enter rather than resume from wherever the document happens to
      // put them next.
      const wrapping = e.shiftKey ? at <= 0 : at === -1 || at === stops.length - 1;
      if (wrapping) {
        e.preventDefault();
        (e.shiftKey ? stops[stops.length - 1] : stops[0])?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  // Focus moves in on open, and back to whatever opened the dialog on close.
  //
  // Kept apart from the effect above, and the dependencies are the reason. This one
  // moves focus, and `onCancel` is a fresh closure on every render at all three call
  // sites — so re-running on that identity would fire the cleanup, dragging the
  // keyboard out to the opener, while somebody is typing in the last-working-day
  // field this dialog holds, which re-renders the parent on every keystroke.
  //
  // Cancel is focused here rather than by `autoFocus` on the button because the
  // opener has to be read *before* focus moves. React applies `autoFocus` while
  // committing, ahead of any effect, so an effect reading `document.activeElement`
  // would find this dialog's own Cancel button and record that as the way back.
  useEffect(() => {
    if (!open) {
      return;
    }
    const opener = document.activeElement;
    cancel.current?.focus();
    return () => {
      // Only when it is still in the document. Confirming often takes the opener
      // with it — the deleted organization's row is gone, and the End button has
      // been replaced by a Revive one — and a detached node cannot hold focus.
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      // Only a click that starts and ends on the backdrop dismisses. Without the
      // target check, releasing the mouse outside after selecting text inside the
      // dialog would close it and lose what the person typed.
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Out of the tab order but able to take focus, so the containment has
        // somewhere to put the keyboard when every control inside is disabled.
        tabIndex={-1}
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-lg focus:outline-none"
      >
        <header className="border-b border-slate-200 px-4 py-2.5">
          <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
        </header>
        <div className="px-4 py-3 text-sm text-slate-700">{children}</div>
        <footer className="flex justify-end gap-2 border-t border-slate-200 px-4 py-2.5">
          <Button ref={cancel} variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}

/** How far an arrow key moves the panel, and how close to an edge it may rest. */
const PANEL_NUDGE = 24;
const PANEL_MARGIN = 8;

/**
 * A window that floats over the page without taking it over.
 *
 * **Deliberately not a modal**, and that is the whole reason it exists: the page
 * behind it stays readable and clickable, so somebody filling in a form can go on
 * searching the list to check what they are copying. Hence no backdrop, no focus
 * trap, nothing inert, and `aria-modal="false"` — a screen reader told "modal"
 * stops offering the rest of the document, which is what a modal dialog is for and
 * exactly what this is not.
 *
 * **Draggable, because a panel fixed in one place always covers the row somebody
 * wants to read.** The header is the handle. Arrow keys move it too: a panel only a
 * mouse can shift is a panel a keyboard user cannot get out of the way.
 *
 * **Escape does not close it.** It holds a part-filled form, and a native date input
 * already uses Escape to dismiss its own picker — so the key arrives here for
 * reasons that have nothing to do with wanting the form gone. Losing typed work to a
 * stray keystroke is worse than reaching for a button. `ConfirmDialog` above closes
 * on Escape and should: it is modal, and it holds one decision rather than a form.
 */
export function FloatingPanel({
  title,
  onClose,
  returnFocusTo,
  className,
  children
}: {
  title: string;
  onClose: () => void;
  /**
   * Where focus goes when the panel closes, looked up at that moment.
   *
   * Capturing the opening element is not enough. The page behind this panel is meant
   * to be used while it is open, so the screen holding that control can unmount and
   * come back — and the node captured on open is then detached while a working
   * replacement sits in the document. A getter is asked at close instead, so it finds
   * whichever control is there now. Falls back to the element that had focus on open.
   */
  returnFocusTo?: () => HTMLElement | null | undefined;
  className?: string;
  children: ReactNode;
}) {
  const labelId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  // Null until it is moved: the panel opens where CSS puts it and only switches to
  // coordinates once somebody drags or nudges it. Keeping the opening position in CSS
  // means it needs no measurement and cannot be wrong before layout.
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);

  // Clamped on every move, so the panel cannot be put somewhere it can never be
  // brought back from: a header dragged past the bottom edge leaves no handle.
  const clamp = (x: number, y: number) => {
    const box = ref.current?.getBoundingClientRect();
    const room = (extent: number, size: number) =>
      Math.max(PANEL_MARGIN, extent - size - PANEL_MARGIN);
    return {
      x: Math.min(Math.max(x, PANEL_MARGIN), room(window.innerWidth, box?.width ?? 0)),
      y: Math.min(Math.max(y, PANEL_MARGIN), room(window.innerHeight, box?.height ?? 0))
    };
  };

  const nudge = (dx: number, dy: number) => {
    const box = ref.current?.getBoundingClientRect();
    const from = at ?? { x: box?.left ?? 0, y: box?.top ?? 0 };
    setAt(clamp(from.x + dx, from.y + dy));
  };

  // Re-clamped when the viewport changes, not only when the panel moves. A position
  // set by dragging to the bottom-right of a wide window is off-screen in a narrow
  // one, and a header nobody can reach is a panel nobody can close or move back.
  useEffect(() => {
    const onResize = () => setAt((prev) => (prev ? clamp(prev.x, prev.y) : null));
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
    // clamp reads the live element on each call, so it needs no dependency of its own.
  }, []);

  // Focus moves in on open, and back to whatever opened it on close.
  //
  // Not a focus *trap* — the page behind stays reachable by design — but a panel that
  // appears without taking focus leaves a keyboard user tabbing through the filters
  // and the whole table to reach the form they just asked for. The header takes it
  // rather than the first field, so a screen reader hears what this is and that the
  // arrow keys move it; the form is one Tab away.
  useEffect(() => {
    const opener = document.activeElement;
    headerRef.current?.focus();
    return () => {
      const asked = returnFocusTo?.();
      if (asked?.isConnected) {
        asked.focus();
        return;
      }
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
    // Deliberately empty deps. `returnFocusTo` is only ever called in the cleanup, so
    // the getter captured on mount is enough — and re-running this on a new function
    // identity would drag focus back into the panel while somebody is using the page
    // behind it, which is the one thing this panel exists not to do.
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    // Anywhere on the header except its controls. The first version required the
    // event's target to *be* the header, which excluded the title sitting on it — so
    // the most obvious thing to grab was the one thing that did not drag.
    if ((e.target as Element).closest("button")) {
      return;
    }
    const box = ref.current?.getBoundingClientRect();
    const grabX = e.clientX - (box?.left ?? 0);
    const grabY = e.clientY - (box?.top ?? 0);
    const handle = e.currentTarget;
    // Pointer capture rather than document listeners: the pointer leaves the header
    // constantly while dragging, and capture keeps the events arriving without a
    // document-level listener to forget to remove.
    handle.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => setAt(clamp(ev.clientX - grabX, ev.clientY - grabY));
    const stop = () => {
      handle.releasePointerCapture(e.pointerId);
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", stop);
      // `pointercancel` matters as much as `pointerup`: an interrupted drag — a
      // context menu, an OS focus change, a pen lifted — releases capture without
      // ever firing pointerup. Without this the move listener stays attached and goes
      // on firing during ordinary hover, teleporting the panel using the grab offset
      // of a drag that ended long ago.
      handle.removeEventListener("pointercancel", stop);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", stop);
    handle.addEventListener("pointercancel", stop);
  };

  const arrows: Record<string, [number, number]> = {
    ArrowLeft: [-PANEL_NUDGE, 0],
    ArrowRight: [PANEL_NUDGE, 0],
    ArrowUp: [0, -PANEL_NUDGE],
    ArrowDown: [0, PANEL_NUDGE]
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby={labelId}
      style={at ? { left: at.x, top: at.y, right: "auto", bottom: "auto" } : undefined}
      className={cn(
        "fixed bottom-4 right-4 z-40 flex max-h-[82vh] w-[min(52rem,calc(100vw-1rem))] flex-col",
        "rounded-lg border border-slate-300 bg-white shadow-2xl",
        className
      )}
    >
      {/* `touch-none` so dragging does not scroll the page underneath at the same
          time, which would move the panel and the list at once. */}
      <header
        ref={headerRef}
        onPointerDown={onPointerDown}
        onKeyDown={(e) => {
          const step = arrows[e.key];
          if (step) {
            e.preventDefault();
            nudge(step[0], step[1]);
          }
        }}
        // `role="group"` so the label is actually exposed: a bare <header> nested in
        // a div maps to no role, and an aria-label on a roleless element is not
        // reliably announced. The group is a real one — it holds the title and the
        // close button — and it is what takes focus for the arrow keys.
        role="group"
        tabIndex={0}
        aria-label={`${title} — drag, or move with the arrow keys`}
        className={cn(
          "flex cursor-move touch-none items-center justify-between gap-3 rounded-t-lg",
          "border-b border-slate-200 bg-slate-50 px-4 py-2.5",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
        )}
      >
        <h2 id={labelId} className="text-sm font-semibold text-slate-800">
          {title}
        </h2>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </header>
      <div className="min-h-0 grow overflow-y-auto p-3">{children}</div>
    </div>
  );
}
